import {Route, Routes} from 'react-router-dom';

import {AccountingModule} from '@dms/accounting';
import {AnalyticsModule} from '@dms/analytics';
import {AuctionModule} from '@dms/auction';
import {BusinessCaseModule} from '@dms/business-case';
import {CommunicationModule} from '@dms/communication';
import {CustomerModule} from '@dms/customer';
import {DemoModule} from '@dms/demo';
import {EmployeeModule} from '@dms/employee';
import {ImportModule} from '@dms/import';
import {InspectionModule} from '@dms/inspection';
import {useInterestModule} from '@dms/interest';
import {MyProfileModule} from '@dms/my-profile';
import {OpportunitiesKanbanModule} from '@dms/opportunities-kanban';
import {OrchestrationModule} from '@dms/orchestration';
import {QuickPurchaseModule} from '@dms/quick-purchase';
import {RecordAuditsModule} from '@dms/recordAudit';
import {RentalOrderModule} from '@dms/rental-order';
import {redirectRoute, restrictedAreaRoutes} from '@dms/routes';
import {SalesLeagueModule} from '@dms/sales-league';
import {SettingsModule} from '@dms/settings/core';
import {ErrorPage, RestrictedArea} from '@dms/shared';
import {SourcingModule} from '@dms/sourcing';
import {TaskManagerModule} from '@dms/task-manager';
import {TelephonyModule} from '@dms/telephony';
import {TestDriveModule} from '@dms/test-drive';
import {TiresInventoryModule} from '@dms/tires-inventory';
import {VehiclesModule} from '@dms/vehicle';
import {WarehouseModule} from '@dms/warehouse/routes';
import {WorkshopModule} from '@dms/workshop/routes';

import {purgeCache} from 'features/rtkq-persist';

import {useRegisterModules} from '../../hooks/useRegisterModules/useRegisterModules';
import {Redirect} from '../../pages/Redirect/Redirect';
import {AiAssistantReachableRoutesSync} from '../AiAssistantReachableRoutesSync/AiAssistantReachableRoutesSync';
import {useMenuDynamicAccess} from '../Layout/hooks/useMenuDynamicAccess';
import {Layout} from '../Layout/Layout';
import {filterMenuItemsByDynamicAccess} from '../Layout/utils/filterMenuItemsByDynamicAccess';
import {flattenMenuItems} from '../Layout/utils/flattenMenuItems';
import {CoreModule} from './components/CoreModule';

export function ModulesContainer() {
  const interestModule = useInterestModule();
  const menuDynamicAccess = useMenuDynamicAccess();

  const modulesCache = useRegisterModules([
    CoreModule,
    SourcingModule,
    TaskManagerModule,
    QuickPurchaseModule,
    CustomerModule,
    VehiclesModule,
    CommunicationModule,
    BusinessCaseModule,
    TestDriveModule,
    AuctionModule,
    RentalOrderModule,
    interestModule,
    DemoModule,
    WorkshopModule,
    WarehouseModule,
    AccountingModule,
    InspectionModule,
    SettingsModule,
    AnalyticsModule,
    SalesLeagueModule,
    EmployeeModule,
    TiresInventoryModule,
    ImportModule,
    OpportunitiesKanbanModule,
    OrchestrationModule,
    RecordAuditsModule,
    TelephonyModule,
    MyProfileModule({purge: purgeCache}),
  ]);

  if (!modulesCache) {
    return null;
  }

  // Menu visibility is filtered reactively — routes stay a superset and the gated
  // pages guard themselves, so registration never waits on dynamic-access data.
  const menuItems = filterMenuItemsByDynamicAccess(modulesCache.menuItems, menuDynamicAccess);
  const createMenuItems = filterMenuItemsByDynamicAccess(
    modulesCache.createMenuItems,
    menuDynamicAccess
  );

  return (
    <>
      <AiAssistantReachableRoutesSync register={modulesCache} dynamicAccess={menuDynamicAccess} />
      <Routes>
        <Route
          element={
            <Layout
              menuItems={menuItems}
              createMenuItems={createMenuItems}
              menuItemsById={flattenMenuItems(menuItems)}
              data-testid="layout"
            />
          }
        >
          {modulesCache.routes.map((props, index) => {
            const {element, path, ...routeProps} = props;
            return <Route key={`${path}-${index}`} {...routeProps} path={path} element={element} />; // eslint-disable-line react/no-array-index-key -- Pre-existing violation
          })}
          <Route path={restrictedAreaRoutes.home} element={<RestrictedArea />} />
          <Route path={redirectRoute} element={<Redirect />} />
        </Route>
        <Route key="app-routes-error" path="*" element={<ErrorPage message="Page Not Found" />} />
      </Routes>
    </>
  );
}
